import { Box } from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { getConfiguration } from "../../../clients/configuration"
import { AccountsApi, Campsite, CampsitesApi, FilterComparator, MemberContactEmail, MemberContactPhone, MemberDataApi, MemberDataAsMember, MemberRelative, MyMembership } from "../../../clients"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { addAlertWithTimeout } from "../../../redux/features/alerts/alertsHelpers"
import { AppAlert } from "../../AppAlerts/AppAlerts"
import { GenericSpinner } from "../../GenericSpinner/GenericSpinner"
import { MyAccountMembershipGeneralInfo } from "./MyAccountMembershipGeneralInfo"
import { MyAccountMembershipContactInfo } from "./MyAccountMembershipContactInfo"
import { MyAccountMembershipCampsites } from "./MyAccountMembershipCampsites"
import { MyAccountMembershipRelatives } from "./MyAccountMembershipRelatives"
import { MyAccountMembershipPayments } from "./MyAccountMembershipPayments"

export const MyAccountMembership = () => {

    const dispatch = useAppDispatch()
    const currentUser = useAppSelector(state => state.currentUser.account)
    const [loading, setLoading] = useState<boolean>(true)
    const [membership, setMembership] = useState<MyMembership | null>(null)
    const [relatives, setRelatives] = useState<MemberRelative[]>([])
    const [memberCampsites, setMemberCampsites] = useState<Campsite[]>([])
    const [memberPhones, setMemberPhones] = useState<MemberContactPhone[]>([])
    const [memberEmails, setMemberEmails] = useState<MemberContactEmail[]>([])
    const [sponsoredByMember, setSponsoredByMember] = useState<MemberDataAsMember | null>(null)

    const loadMembership = useCallback(async () => {
        const config = await getConfiguration()
        const accountApi = new AccountsApi(config)
        const campsiteApi = new CampsitesApi(config)
        const memberDataApi = new MemberDataApi(config)
        setLoading(true)
        try {
            const response = await accountApi.getAccountMembership({
                accountID: currentUser?.id || ''
            })
            setMembership(response.data)
            if (response.data) {
                const [
                    initialRelatives,
                    memberCampsiteRelation,
                    initialPhones,
                    initialEmails
                ] = await Promise.all([
                    accountApi.getAccountMembershipRelatives({ accountID: currentUser.id }),
                    accountApi.getAccountMembershipCampsites({ accountID: currentUser.id }),
                    accountApi.getAccountMembershipContactPhones({ accountID: currentUser.id }),
                    accountApi.getAccountMembershipContactEmails({ accountID: currentUser.id })
                ])
                const campsites = await campsiteApi.listCampsites({
                    filters: [
                        {
                            field: 'id',
                            comparator: FilterComparator.In,
                            value: memberCampsiteRelation.data.map((campsite) => campsite.campsiteId)
                        }
                    ]
                })
                if (response.data.sponsoredByMemberId) {
                    const sponsoredByMember = await memberDataApi.getMemberData({
                        memberID: response.data.sponsoredByMemberId
                    })
                    setSponsoredByMember(sponsoredByMember.data)
                }
                setRelatives(initialRelatives.data)
                setMemberCampsites(campsites.data)
                setMemberPhones(initialPhones.data)
                setMemberEmails(initialEmails.data)
            }
        } catch {
            addAlertWithTimeout(dispatch, new AppAlert('error', 'Failed to load membership information'))
        } finally {
            setLoading(false)
        }
    }, [dispatch, currentUser.id])

    useEffect(() => {
        loadMembership()
    }, [loadMembership])

    if (loading) {
        return <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}><GenericSpinner /></Box>
    }

    if (!membership) {
        return <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}>No membership information found</Box>
    }

    return (
        <Box className="my-account-membership__container" sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: '8px', pb: 3 }}>
            <MyAccountMembershipGeneralInfo sx={{ flex: '1 1 calc(50% - 8px)' }} membership={membership} sponsoredByMember={sponsoredByMember} />
            <MyAccountMembershipContactInfo sx={{ flex: '1 1 calc(50% - 8px)' }} phones={memberPhones} emails={memberEmails} />
            <MyAccountMembershipCampsites sx={{ flex: '1 1 100%' }} campsites={memberCampsites} />
            <MyAccountMembershipRelatives sx={{ flex: '1 1 100%' }} relatives={relatives} />
            {
                membership && <MyAccountMembershipPayments sx={{ flex: '1 1 100%' }} memberID={membership.id} />
            }
        </Box>
    )
}